import api from '@/helpers/api_helper';
import { apiUrls, infra } from '@/constant/configure';
import { upperCase } from 'lodash';

import sessionStore from '@/store/modules/sessionStore';
import { checkExpireTime } from '@/utils/checkExpireTime';
import { AES, enc } from 'crypto-js';
// import * as process from "process";
import { checkMallPermission } from '@/services/mall.service';
import { checkOpenState } from '@/services/open-mall.service';
import userStore from '@/store/modules/userStore';
import spaceUserV2Store from '@/store/modules/spaceUserV2Store';
import spaceUserStore from '@/store/modules/spaceUserStore';
import currentMallStore from '@/store/modules/currentMallStore';
import { getSpaceUserData } from '@/services/studio.service';
const { setSession, clearSession } = sessionStore.getState();
const { setUserInfo, clearUser } = userStore.getState();
const { clearUser: clearSpaceData } = spaceUserV2Store.getState();
const { clearUser: clearSpaceV2Data } = spaceUserStore.getState();
const { clearCurrentMall } = currentMallStore.getState();

const commonApiBase = apiUrls.common;
const userApiBase = apiUrls.user;
const mallMainApiBase = apiUrls.mall_main;
const studioApiBase = apiUrls.studio;

export const getAnonymousToken = async () => {
    try {
        const storageKey = 'public-token';

        const storage = window.sessionStorage;
        const storedToken = storage.getItem(storageKey);
        const decrypted = !!storedToken ? AES.decrypt(storedToken, storageKey).toString(enc.Utf8) : ``;
        if (!!storedToken) {
            const storedExpired = decrypted.split(';')[1];
            if (!!storedExpired && checkExpireTime(storedExpired, 150)) return decrypted.split(';')[0];
        }

        const { access_token, expires_in } = await api
            .post(
                '/auth/web/getToken',
                {
                    api_key: import.meta.env.VITE_APP_API_PUBLIC_KEY,
                    api_secret: import.meta.env.VITE_APP_API_PUBLIC_SECRET,
                },
                {
                    baseURL: commonApiBase,
                    headers: {
                        Authorization: `Bearer`,
                    },
                },
            )
            .then((r) => {
                return r.data;
            })
            .catch((err) => {
                console.error(err);
                return {
                    access_token: '',
                };
            });

        const encrypted = !!access_token ? AES.encrypt(`${access_token};${expires_in}`, storageKey).toString() : '';
        if (encrypted) {
            storage.setItem(storageKey, encrypted);
        } else {
            storage.removeItem(storageKey);
        }

        if (!!access_token) api.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;

        return access_token || 'test-anonymous-token';
    } catch (e) {
        console.error(e);
    }
};

export const removeAnonymousToken = () => {
    const storageKey = 'public-token';
    sessionStorage.removeItem(storageKey);
};

export const getOwnMalls = async (accessToken?: string) => {
    try {
        const config = accessToken
            ? {
                  headers: {
                      Authorization: `Bearer ${accessToken}`,
                  },
              }
            : {};

        const { ownMallList }: { ownMallList: Record<string, any>[] } = await api
            .get('/mall/own-mall', config)
            .then((r) => {
                const info = r.data;
                if (!!info?.ownMallList && !info.ownMallList?.length)
                    throw {
                        ...r,
                        code: 'not-exist-own-mall',
                    };

                return info;
            });

        const activeMalls = ownMallList.filter((o) => o?.runningState === 'COMPLETED');

        const persistenceMall = activeMalls[0];

        return {
            persistenceMallId: persistenceMall.mallId,
            ownMallList: activeMalls,
        };
    } catch (e) {
        console.log('getOwnMalls-error');
        throw e;
    }
};

export const getToken = async (userId: string, userSecret: string) => {
    try {
        if (!userId || !userSecret) return {};

        const { access_token, expires_in } = await api
            .post(
                '/auth/web/getToken',
                { user_id: userId, user_secret: userSecret },
                {
                    baseURL: commonApiBase,
                },
            )
            .then((r) => {
                return r.data;
            });

        if (!!access_token) api.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;

        return {
            accessToken: access_token,
            expiresIn: expires_in,
        };
    } catch (e) {
        throw e;
    }
};

export const checkRefreshToken = async () => {
    try {
        return await api
            .post(
                '/auth/web/refreshToken/check',
                {},
                {
                    baseURL: commonApiBase,
                },
            )
            .then((r) => {
                return r.data;
            });
    } catch (e) {
        console.log('refresh-error');
        console.error(e);
        return false;
        // throw e;
    }
};

export const refreshToken = async () => {
    try {
        const { access_token, expires_in } = await api
            .post(
                '/auth/web/refreshToken',
                {},
                {
                    baseURL: commonApiBase,
                },
            )
            .then((r) => {
                // removeAnonymousToken();
                return r.data;
            });

        if (!!access_token) api.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;

        return {
            accessToken: access_token,
            expiresIn: expires_in,
        };
    } catch (e) {
        console.log('refresh-error');
        throw e;
    }
};

export const signInSession = async (accountEmail: string, password: string) => {
    try {
        // 1. signIn - (post) /mall/sign-in/formal
        const { user_id: userId, user_secret: userSecret } = await api
            .post(
                '/sign-in',
                {
                    email: accountEmail,
                    password: password,
                },
                {
                    headers: {
                        Authorization: `Bearer ${await getAnonymousToken()}`,
                    },
                    baseURL: userApiBase,
                },
            )
            .then((r) => {
                const info = r.data;
                // if (!info.mallId) throw {
                //     ...r,
                //     code: 'not-exist-own-mall'
                // };

                return info;
            });
        console.log('sign-in/formal:succeed', {
            userId,
            userSecret,
        });

        console.log('userId', userId);

        // 2. getToken - (post) /auth/web/getToken
        console.log('%c test-login-get-token', 'color:red;background-color:yellow;', { userId, userSecret });
        const { accessToken, expiresIn } = await getToken(userId, userSecret);

        const session = {
            userId: userId,
            // mallUserId: mallUserId,
            mallId: '',
            accessToken: accessToken,
            expiresIn: expiresIn,
        };

        // const {setSession} = sessionStore.getState();
        setSession(session);

        console.log('signIn-session', session); //

        // 로그인 성공 후 space 정보를 가져와서 currentMallStore에 저장
        try {
            const spaceData = await getSpaceUserData(userId);
            if (spaceData?.path) {
                const { setCurrentMall } = currentMallStore.getState();
                setCurrentMall({
                    id: userId,
                    name: spaceData.nickname || '',
                    description: spaceData.description || '',
                    mallPath: spaceData.path,
                });
                console.log('Space data loaded and stored:', spaceData.path);
            }
        } catch (error) {
            console.error('Failed to load space data after login:', error);
            // space 데이터 로드 실패해도 로그인은 계속 진행
        }

        return session;

        // get-mall??
    } catch (e) {
        throw e;
    }
};

export const getUserId = async () => {
    try {
        return await api
            .post(
                `/auth/web/user`,
                {},
                {
                    baseURL: commonApiBase,
                },
            )
            .then((r) => {
                return r.data?.user_id || r?.data;
            });
    } catch (e: any) {
        if (typeof e === 'string') {
            throw {
                message: e,
            };
        } else {
            // throw e;
            const { place_holder = undefined } = e.data;
            if (!!place_holder) {
                throw {
                    ...e,
                    data: {
                        ...e.data,
                        placeHolder: place_holder,
                    },
                };
            } else {
                throw e;
            }
        }
    }
};

export const checkSwitchSession = async (): Promise<'UNDEFINED' | 'WAITING' | 'COMPLETED' | 'REFUSAL' | undefined> => {
    try {
        // 1. checkSession
        const checkSession = await checkRefreshToken();
        // console.log('checkSession', checkSession)
        if (!checkSession) return undefined;

        // 2. refreshToken
        const { accessToken, expiresIn } = await refreshToken();
        const mallStatus = await checkOpenState(accessToken);

        // if (mallStatus !== 'COMPLETED') {
        //     console.log('%c clear!!!!', 'color:red;')
        //     clearSession();
        // }
        clearSession();

        console.log();

        return mallStatus || 'UNDEFINED';
    } catch (e: any) {
        console.error(e);
        await signOutSession();
        return undefined;
    }
};

export const checkMallSession = async () => {
    // accessToken 포함 전체 세션체크
    try {
        const storedSession = sessionStore.getState();
        const checkedIn = sessionStorage.getItem('session-type') === 'mall-session';

        console.log('checkedIn', { checkedIn, session: sessionStorage.getItem('session-type') });

        if (!!storedSession?.accessToken && !!storedSession?.mallId && checkedIn) {
            if (checkExpireTime(storedSession.expiresIn, 150)) return true;
        }

        // 1. checkSession
        const checkSession = await checkRefreshToken();
        console.log('checkSession', checkSession);

        // console.log('checkSession', checkSession)
        if (!checkSession) return false;

        // 2. refreshToken
        const { accessToken, expiresIn } = await refreshToken();
        const userId = await getUserId().catch((err) => {
            console.log('get user id failed-checkMallSession');
            throw err;
        });

        // 3. own-mall
        const { persistenceMallId } = await getOwnMalls(accessToken);

        const session = {
            userId: userId,
            // mallUserId: mallUserId,
            mallId: persistenceMallId,
            accessToken: accessToken,
            expiresIn: expiresIn,
        };

        setSession(session);

        sessionStorage.setItem('session-type', 'mall-session');

        // 세션 체크 시에도 space 정보를 가져와서 currentMallStore에 저장
        try {
            const spaceData = await getSpaceUserData(userId);
            if (spaceData?.path) {
                const { setCurrentMall } = currentMallStore.getState();
                setCurrentMall({
                    id: userId,
                    name: spaceData.nickname || '',
                    description: spaceData.description || '',
                    mallPath: spaceData.path,
                });
                console.log('Space data loaded during session check:', spaceData.path);
            }
        } catch (error) {
            console.error('Failed to load space data during session check:', error);
            // space 데이터 로드 실패해도 세션 체크는 계속 진행
        }

        return true;

        // get-mall??
    } catch (e) {
        return false;
    }
};
export const checkUserSession = async () => {
    // accessToken 포함 전체 세션체크
    try {
        const storedSession = sessionStore.getState();
        const checkedIn = sessionStorage.getItem('session-type') === 'mall-session';

        console.log('checkedIn', { checkedIn, session: sessionStorage.getItem('session-type') });

        if (storedSession?.accessToken && storedSession?.mallId && checkedIn) {
            if (checkExpireTime(storedSession.expiresIn, 150)) return true;
        }

        // 1. checkSession
        const checkSession = await checkRefreshToken();
        console.log('checkSession', checkSession);

        // console.log('checkSession', checkSession)
        if (!checkSession) return false;

        // 2. refreshToken
        const { accessToken, expiresIn } = await refreshToken();
        const userId = await getUserId().catch((err) => {
            console.log('get user id failed-checkMallSession');
            throw err;
        });

        // 3. own-mall
        // const { persistenceMallId } = await getOwnMalls(accessToken);

        const session = {
            userId: userId,
            // mallUserId: mallUserId,
            mallId: '',
            accessToken: accessToken,
            expiresIn: expiresIn,
        };

        setSession(session);

        sessionStorage.setItem('session-type', 'mall-session');

        // 사용자 세션 체크 시에도 space 정보를 가져와서 currentMallStore에 저장
        try {
            const spaceData = await getSpaceUserData(userId);
            if (spaceData?.path) {
                const { setCurrentMall } = currentMallStore.getState();
                setCurrentMall({
                    id: userId,
                    name: spaceData.nickname || '',
                    description: spaceData.description || '',
                    mallPath: spaceData.path,
                });
                console.log('Space data loaded during user session check:', spaceData.path);
            }
        } catch (error) {
            console.error('Failed to load space data during user session check:', error);
            // space 데이터 로드 실패해도 세션 체크는 계속 진행
        }

        return true;

        // get-mall??
    } catch (e) {
        return false;
    }
};

export const signOutSession = async () => {
    try {
        clearSession();
        clearUser();
        clearSpaceData();
        clearSpaceV2Data();
        clearCurrentMall();
        clearTemporaryToken();
        sessionStorage.removeItem('session-type');

        const signOutCall = await api
            .post(
                '/auth/web/sign-out',
                {},
                {
                    baseURL: commonApiBase,
                },
            )
            .then(async (r) => {
                api.defaults.headers.common['Authorization'] = `Bearer ${await getAnonymousToken()}`;
            });
    } catch (e) {
        console.error(e);
    }
};

export const requestEmailVerifyCode = async (email: string) => {
    try {
        await api.post(
            `/email/pre-signup/request`,
            {
                email: email,
            },
            {
                baseURL: userApiBase,
            },
        );
    } catch (e) {
        if (typeof e === 'string') {
            throw {
                message: e,
            };
        } else {
            throw e;
        }
    }
};

export const requestUnRegistrationEmailVerifyCode = async (email: string) => {
    try {
        await api.post(
            `/email/membership-withdrawal/request`,
            {
                email: email,
            },
            {
                baseURL: userApiBase,
            },
        );
    } catch (e: any) {
        if (typeof e === 'string') {
            throw {
                message: e,
            };
        } else {
            const { place_holder } = e.data;
            if (!!place_holder) {
                throw {
                    ...e,
                    data: {
                        ...e.data,
                        placeHolder: place_holder,
                    },
                };
            } else {
                throw e;
            }
        }
    }
};

export const deleteAccountWithEmail = async (payload: { email: string; password: string; auth_code: string }) => {
    try {
        await api.post('/membership-withdrawal', payload, {
            baseURL: userApiBase,
        });
    } catch (e: any) {
        if (typeof e === 'string') {
            throw {
                message: e,
            };
        } else {
            const { place_holder } = e.data;
            if (!!place_holder) {
                throw {
                    ...e,
                    data: {
                        ...e.data,
                        placeHolder: place_holder,
                    },
                };
            } else {
                throw e;
            }
        }
    }
};

export const credentialEmailVerifyCode = async (email: string, code: string) => {
    try {
        await api.post(
            `/email/pre-signup/authenticate`,
            {
                email: email,
                auth_code: code,
            },
            {
                baseURL: userApiBase,
            },
        );
    } catch (e) {
        if (typeof e === 'string') {
            throw {
                message: e,
            };
        } else {
            throw e;
        }
    }
};

export type SignUpWithEmailPasswordRequestBody = {
    nickname: string;
    email: string;
    password: string;
    age_group?: string;
    gender?: string;
    image_url?: string;
    invitation_user_code?: string;
    phone_number?: string;
};
export const signUpWithEmailPassword = async (data: SignUpWithEmailPasswordRequestBody) => {
    try {
        return await api
            .post(`/sign-up`, data, {
                baseURL: userApiBase,
            })
            .then((r) => {
                return r.data;
            });
    } catch (e) {
        if (typeof e === 'string') {
            throw {
                message: e,
            };
        } else {
            throw e;
        }
    }
};

export const setTemporaryToken = (accessToken: string) => {
    try {
        sessionStorage.setItem('session_token', accessToken);
    } catch (e) {
        if (typeof e === 'string') {
            throw {
                message: e,
            };
        } else {
            throw e;
        }
    }
};

export const getTemporaryToken = () => {
    try {
        return sessionStorage.getItem('session_token');
    } catch (e) {
        if (typeof e === 'string') {
            throw {
                message: e,
            };
        } else {
            throw e;
        }
    }
};

export const clearTemporaryToken = () => {
    try {
        return sessionStorage.removeItem('session_token');
    } catch (e) {
        if (typeof e === 'string') {
            throw {
                message: e,
            };
        } else {
            throw e;
        }
    }
};

export const sendPasswordResetMail = async (email: string) => {
    try {
        await api.post(
            `/password/email/renew/request`,
            {
                email: email,
            },
            {
                baseURL: userApiBase,
            },
        );
    } catch (e: any) {
        if (typeof e === 'string') {
            throw {
                message: e,
            };
        } else {
            // throw e;
            const { place_holder } = e.data;
            if (!!place_holder) {
                throw {
                    ...e,
                    data: {
                        ...e.data,
                        placeHolder: place_holder,
                    },
                };
            }
        }
    }
};

export const patchNickname = async (userId: string, nickname: string) => {
    try {
        await api.patch(`/${userId}/nickname?nickname=${nickname || ''}`, {}, { baseURL: apiUrls.user });
    } catch (error) {
        throw error;
    }
};

export const getUserData = async (user_id: string) => {
    try {
        const userData = await api.post(`/profile/find`, { user_id }, { baseURL: userApiBase }).then((r) => r.data);
        setUserInfo(userData);
        return userData;
    } catch (error) {
        return {};
    }
};

export const socialLogin = async (body: { social: string; code: string; username?: string }) => {
    try {
        const { userId, userSecret } = await api
            .post(`/login/${body.social}`, { ...body }, { baseURL: userApiBase })
            .then((r) => r.data);
        const { accessToken, expiresIn } = await getToken(userId, userSecret);
        const session = {
            accessToken: accessToken,
            expiresIn: expiresIn,
            userId: userId,
            mallId: '',
        };
        setSession(session);
        return session;
    } catch (error) {
        throw error;
    }
};

export const updateUserData = async (userId: string, payload: any) => {
    try {
        return await api.patch(`/${userId}/profile`, { ...payload }, { baseURL: userApiBase }).then((r) => r);
    } catch (error) {
        throw error;
    }
};

export const getMallUserIdByToken = async (): Promise<string> => {
    try {
        const checkId = await api
            .post(`/mall-user-id/permission`, {}, { baseURL: mallMainApiBase })
            .then((r) => r.data);
        return !!checkId
            ? await api.get(`/mall-user-id`, { baseURL: mallMainApiBase }).then((r) => r.data?.mallUserId || '')
            : await api.post(`/mall-user-id`, {}, { baseURL: mallMainApiBase }).then((r) => r.data?.mallUserId || '');
    } catch (error) {
        return '';
    }
};
export const getUserSNSInfo = async () => {
    try {
        return await api.get(`/users/sns-info`, { baseURL: studioApiBase }).then((r) => r.data?.item || {});
    } catch (error) {
        throw error;
    }
};
export const putUserSNSInfo = async (instagramId: string) => {
    // 포인트 2배 적립 이전에 유저 인플루언서 인증 API이기 때문에 auth service 파일에 있음
    try {
        return await api.put(`/users/sns-info`, { instagramId }, { baseURL: studioApiBase });
    } catch (error) {
        throw error;
    }
};
export const postConfirmTerms = async (
    userId: string,
    payload: { smsMarketingConsented: boolean; emailMarketingConsented: boolean },
) => {
    try {
        return await api.post(`/users/${userId}/terms?userId=${userId}`, { ...payload }, { baseURL: studioApiBase });
    } catch (error) {
        throw error;
    }
};

const authService = {
    signInSession,
    signOutSession,
    checkRefreshToken,
    checkMallSession,
};

export default authService;
